import { useValidity } from "capitalrx-components-2.0/lib/forms";
import { Input as TextboxInput } from "capitalrx-components-2.0/lib/forms/Textbox/Input";
import { Overlay, Panel, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { CSSProperties, forwardRef, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { TypeaheadTextboxProps } from "./TypeaheadTextbox";

const MenuItemDiv = styled.div`
  cursor: pointer;
  padding: 0.5rem;

  &:hover {
    background-color: #f1f1f1;
  }
`;

export interface InputProps extends TypeaheadTextboxProps<any, any> {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * The input type.
   */
  readonly type?: "text" | "password";
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    value,
    required,
    maxLength,
    defaultValue,
    onFocusEnter,
    onFocusExit,
    dataSource,
    openOnFocus,
    onItemSelected,
    onChange,
    shouldOpen,
    ...restProps
  } = props;

  const { busy, status } = dataSource;

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [invalid] = useValidity();

  const onInputFocusEnter = useCallback(() => {
    if (openOnFocus) {
      setIsOverlayVisible(true);
    }
    onFocusEnter?.();
  }, [openOnFocus]);

  const onInputFocusExit = useCallback(() => {
    if (isOverlayVisible) {
      setIsOverlayVisible(false);
    }
    onFocusExit?.();
  }, [isOverlayVisible, onFocusExit]);

  const onInputChange = useCallback(
    (value: string, previousValue: string) => {
      onChange?.(value, previousValue);
      if (!isOverlayVisible && shouldOpen?.(value)) {
        setIsOverlayVisible(true);
      } else if (shouldOpen?.(value) === false) {
        setIsOverlayVisible(false);
      }
    },
    [onChange, isOverlayVisible],
  );

  /**
   * Pull the overlay up over top of the error text that may or may not be displayed
   */
  const overlayStyle = useMemo(() => {
    if (invalid) {
      return { transform: "translateY(-10px)" };
    } else {
      return {};
    }
  }, [invalid]);

  const panelStyle = useMemo(() => {
    const baseStyle = { padding: "0", width: "100%", position: "fixed", zIndex: 10000, overflowY: "scroll", overflowX: "hidden", maxHeight: "400px" };
    if(busy || status === "error") {
      return {
        ...baseStyle,
        overflowY: "hidden",
        overflowX: "hidden",
      } as CSSProperties;
    } else {
      return { ...baseStyle } as CSSProperties;
    }
  }, [busy, status]);

  return (
    <>
      <TextboxInput
        {...restProps}
        ref={ref}
        value={value}
        type="text"
        required={required}
        maxLength={maxLength}
        defaultValue={defaultValue}
        onFocusExit={onInputFocusExit}
        onFocusEnter={onInputFocusEnter}
        onChange={onInputChange}
      />
      <Overlay style={overlayStyle}>
        {isOverlayVisible && (
          <Panel style={panelStyle}>
            {busy && (
              <Shelf style={{ padding: "0.5rem" }}>
                <Spinner />
                <Shelf.Spacer />
              </Shelf>
            )}
            {!busy && (
              <>
                {status !== "error" && (
                  <Stack spacing="none">
                    {dataSource.rows.map((row) => {
                      return (
                        <MenuItemDiv onMouseDown={() => onItemSelected(row.id)} key={row.id}>
                          <Body>{row.data.displayText}</Body>
                        </MenuItemDiv>
                      );
                    })}
                  </Stack>
                )}
                {status === "error" && (
                  <Shelf style={{ padding: "0.5rem" }}>
                    <Body>No results found</Body>
                  </Shelf>
                )}
              </>
            )}
          </Panel>
        )}
      </Overlay>
    </>
  );
});
